import './analysisComponent.scss';
import React, {useCallback, useEffect, useState} from 'react';
import {SvgIcon} from '../icons/svgIcon';
import {Icons} from '../icons/icons';
import {useTranslation} from 'react-i18next';
import {debounce} from 'lodash';
import {useSearchPageType} from '../search/search-hooks';
import {doAnalyzeRequest} from '../services/analyze-service';
import {SearchPageType} from '../search/search-config';
import {Aggregations, HibuTag, HibuTags, Span} from '../search/search-types';
import {TagListComponent} from './tagListComponent';
import {mapTags} from '../search/search.utils';
import {Highlight, HighlightsSeparatedByType} from './analyze-types';
import {LanguageSwitcher} from '../languageSwitcher/languageSwitcher';
import {Language} from '../i18nTypes';
import {HighlightWithinTextarea} from 'react-highlight-within-textarea';
import {AggregationListComponent} from './aggregationListComponent';
import {useLocalizationUtil} from '../localizationUtil';

type InputProps = {
    readonly inputTitle: string;
    readonly resultsTitle: string;
    readonly searchPageType: SearchPageType;
};

//TODO full height for textarea
export const AnalysisComponent = React.memo((props: InputProps) => {
    const {t} = useTranslation();
    const searchPageType = useSearchPageType();
    const {selectedLanguage} = useLocalizationUtil();
    const [inputValue, setInputValue] = useState<string>('');
    const [tags, setTags] = useState<HibuTags>({});
    const [aggregations, setAggregations] = useState<Aggregations>({});
    const [highlight, setHighlight] = useState<Highlight[]>([]);
    const NON_ASCII_NL_EXT = /[^\x20-\xFF\n]+/g

    useEffect(() => {
        if (inputValue) {
            doAnalyzeRequest(inputValue, searchPageType, selectedLanguage, handleResult);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedLanguage]);
    useEffect(() => {
        if (inputValue) {
            doAnalyzeRequest(inputValue, searchPageType, selectedLanguage, handleResult);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedLanguage]);

    const handleTagSelected = (tag: HibuTag): void => {
        handleHighlighting(tags, tag.id);
    };
    const handleTagDeselected = (): void => {
        handleHighlighting(tags);
    };

    const onClearInput = () => {
        setInputValue('');
        setHighlight([]);
        setTags({});
        setAggregations({});
    };

    const onChange = (newValue: string) => {
        if (newValue === inputValue) {
            return;
        }
        newValue = newValue.replace(NON_ASCII_NL_EXT, '');
        setInputValue(newValue);
        setHighlight([]);
        setTags({});
        setAggregations({});
        debounceInputUpdate(newValue);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debounceInputUpdate = useCallback(
        debounce((input: string) => {
            if (input) {
                doAnalyzeRequest(input, searchPageType, selectedLanguage, handleResult);
            }
        }, 1000),
        [selectedLanguage]
    );

    const handleResult = (res: Record<string, any>): void => {
        const aggregations = res.aggregations;
        setAggregations(aggregations);
        const tags = mapTags(res.tags, selectedLanguage, res.language);
        setTags(tags);
        handleHighlighting(tags);
    };

    const handleHighlighting = (tags: HibuTags, tagId?: string): void => {
        let highlights: HighlightsSeparatedByType = {};
        Object.keys(tags).forEach((type) => {
            if (type === 'doctype') {
                return;
            }
            for (let i = 0; i < tags[type].length; i++) {
                const spans = tags[type][i].spans;
                if (spans) {
                    for (let j = 0; j < spans.length; j++) {
                        highlights = fillUpSpans(highlights, type, tags[type][i].id, spans[j], tagId);
                    }
                }
            }
        });
        const arr: any = [];
        for (const allHighlightsKey in highlights) {
            arr.push(highlights[allHighlightsKey]);
        }
        setHighlight(arr);
    };

    const fillUpSpans = (
        highlights: HighlightsSeparatedByType,
        type: string,
        spanId: string,
        span: Span,
        tagId?: string
    ): HighlightsSeparatedByType => {
        if (!tagId || tagId === spanId) {
            if (highlights[type] && Object.prototype.hasOwnProperty.call(highlights, type)) {
                highlights[type]?.highlight?.push([span.start, span.end]);
            } else {
                highlights[type] = {
                    highlight: [[span.start, span.end]],
                    className: 'highlight-' + type,
                };
            }
        }
        return highlights;
    };

    const languageOptionsMap = {
        [Language.DEFAULT]: {value: Language.DEFAULT, label: t('analysis.language.autodetect')},
        [Language.ENGLISH]: {value: Language.ENGLISH, label: 'En'},
        [Language.GERMAN]: {value: Language.GERMAN, label: 'De'},
        [Language.FRENCH]: {value: Language.FRENCH, label: 'Fr'},
        [Language.ITALIAN]: {value: Language.ITALIAN, label: 'It'},
    };

    return (
        <div id="hibu_analysis">
            <div id="hibu_analysis_content">
                <div id="hibu_analysis_top_row">
                    <div id="analysis_input_title">
                        <h1>{props.inputTitle}</h1>
                    </div>
                    <h1 id="analysis_results_title">{props.resultsTitle}</h1>
                </div>
                <div id="hibu_analysis_main_row">
                    <div id="hibu_analysis_input_column">
                        <div id="hibu_analysis_input">
                            <div id="analysis_language_switcher" className="sort-dropdown">
                                <span className="sort-dropdown-label">{t('attributes.language')}</span>
                                <LanguageSwitcher
                                    id="language-switcher-analysis"
                                    isDarkBackground={false}
                                    languageOptionsMap={languageOptionsMap}
                                    defaultValue={Language.DEFAULT}
                                />
                            </div>
                            {inputValue.match('\\S+') !== null && (
                                <SvgIcon
                                    id="hibu_input_text_clear"
                                    icon={Icons.X}
                                    alt={t('general.close')}
                                    onClick={onClearInput}
                                />
                            )}
                            <div id="analysis-textarea">
                                <HighlightWithinTextarea
                                    value={inputValue}
                                    highlight={highlight}
                                    onChange={(value: string) => onChange(value)}
                                    placeholder=""
                                />
                            </div>
                        </div>
                    </div>
                    <div id="hibu_analysis_result_column">
                        <TagListComponent
                            tags={tags}
                            handleTagSelected={handleTagSelected}
                            handleTagDeselected={handleTagDeselected}
                        />
                        <AggregationListComponent aggregations={aggregations} />
                    </div>
                </div>
            </div>
        </div>
    );
});
